/**
 * @fileoverview Update My Page target stage mutation hook.
 * @description Moves a target to another stage (POST /sales/targets/{targetId}/stage) with the `tg1.` version
 * the dialog showed as If-Match, so a target that changed since is refused (`412`) and shown for
 * review rather than overwritten.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { SALES_QUERY_KEY } from '@/features/sales/constants/prospects-query-keys.constants'
import { updateMyPageTargetStage } from '@/features/sales/services/my-page-targets.service'

import type { MyPageUpdateStageInput } from '@/features/sales/types/my-page.types'

/**
 * @description Sends the request, then refreshes every sales query so the table, KPIs and
 * details show the target's new state.
 * @param {() => void} [onSuccess] - Called after a success, even when the dialog has unmounted
 * since: it is an option of the mutation, not of a `mutate` call.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the target id, the version shown and the new stage).
 */
export function useUpdateMyPageTargetStage(onSuccess?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      targetId,
      version,
      input,
    }: {
      targetId: string
      version: string
      input: MyPageUpdateStageInput
    }) => updateMyPageTargetStage(targetId, version, input),
    /**
     * @description Refreshes every sales query, then reports the success.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY })
      onSuccess?.()
    },
  })
}
